/*
 * Bundle product page (sections/main-bundle.liquid).
 *
 * The bundle product is a display product: "Add bundle" adds the real component
 * variants to the cart in one request, tagged with _bundle / _bundle_id line
 * properties. Prices shown here are a preview of the bundle discount; the
 * discount itself is applied at checkout by the bundle discount function.
 */
(() => {
  if (customElements.get('bundle-product')) return;

  // Size option names across the stores (BG, GR/CY, RO, RS), compared trimmed and lowercased.
  const SIZE_OPTION_NAMES = [
    'size',
    'размер',
    'размери',
    'за размер матрак',
    'размер на матрак',
    'μέγεθος',
    'διαστάσεις',
    'για μέγεθος στρώματος',
    'dimensiuni',
    'dimensiune',
    'dimensiuni pat',
    'dimensiuni saltea',
    'mărime',
    'by mattress size',
    'dimenzije',
    'dimenzija',
    'veličina',
    'za dimenziju dušeka',
  ];

  // "160/200", "160x200", "160х200" (Cyrillic х) -> ["160x200"]
  // "160x190/200" (protectors that fit both lengths) -> ["160x190", "160x200"]
  const canonicalSizes = (value) => {
    const clean = String(value ?? '')
      .toLowerCase()
      .replace(/[х×]/g, 'x')
      .replace(/\s|см|cm/g, '');
    const match = clean.match(/^(\d+)[x/](\d+)(?:\/(\d+))?$/);
    if (!match) return [clean];
    const [, width, length, altLength] = match;
    return altLength ? [`${width}x${length}`, `${width}x${altLength}`] : [`${width}x${length}`];
  };

  const displaySize = (size) => size.replace('x', '/');

  const sizeOptionIndex = (product) =>
    product.options.findIndex((name) => SIZE_OPTION_NAMES.includes(String(name).trim().toLowerCase()));

  const variantSizes = (product, variant) => {
    const index = sizeOptionIndex(product);
    return index === -1 ? [] : canonicalSizes(variant.options[index]);
  };

  const productSizes = (product) => {
    const sizes = [];
    product.variants.forEach((variant) => {
      variantSizes(product, variant).forEach((size) => {
        if (!sizes.includes(size)) sizes.push(size);
      });
    });
    return sizes;
  };

  const variantsForSize = (product, size) =>
    product.variants.filter((variant) => variantSizes(product, variant).includes(size));

  const firstAvailable = (variants) => variants.find((variant) => variant.available) || variants[0] || null;

  const variantForSize = (product, size) => firstAvailable(variantsForSize(product, size));

  const defaultVariant = (product) => firstAvailable(product.variants);

  // Options the customer still has to choose once the size is set, e.g. a bed's base height and legs.
  // A component that doesn't follow the bundle size (e.g. a kids' mattress) chooses its size here too.
  const extraOptionIndexes = (product, includeSize = false) => {
    const sizeIndex = sizeOptionIndex(product);
    return product.options
      .map((name, index) => index)
      .filter(
        (index) =>
          (includeSize || index !== sizeIndex) &&
          new Set(product.variants.map((variant) => variant.options[index])).size > 1,
      );
  };

  // What a product costs outside the bundle: its price less its own running promotion.
  // The product's own promotion on a line: a percent of the product, or a fixed amount per unit of the
  // variant (some stores); at checkout the larger one applies.
  const promoDiscount = (product, variant, amount, quantity = 1) =>
    Math.max(Math.round((amount * (Number(product.promoPercent) || 0)) / 100), (Number(variant?.promoAmount) || 0) * quantity);

  const promoPrice = (product, variant, amount, quantity = 1) => amount - promoDiscount(product, variant, amount, quantity);

  const formatMoney = (cents) => window.theme.formatMoney(cents, window.theme.moneyFormat);

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[char]);

  class BundleProduct extends HTMLElement {
    connectedCallback() {
      this.bundle = JSON.parse(this.querySelector('[data-bundle-json]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-bundle-strings]').textContent);

      // A component can be limited to some variants, e.g. only the double sizes of a mattress.
      this.bundle.components.forEach((component) => {
        const allowed = component.variantIds || [];
        if (!allowed.length) return;
        component.products = component.products
          .map((product) => ({...product, variants: product.variants.filter((variant) => allowed.includes(variant.id))}))
          .filter((product) => product.variants.length);
      });
      // Per component: index of the chosen product. An optional step starts without one (-1):
      // the customer adds it, and can remove it again.
      this.selectedProducts = this.bundle.components.map((component) => (component.optional ? -1 : 0));
      // Per component: option index -> chosen value, for the options beyond size,
      // and the order the options were chosen in (latest last).
      this.selectedOptions = this.bundle.components.map(() => ({}));
      this.optionOrder = this.bundle.components.map(() => []);
      this.selectedAddons = new Set();
      this.size = null;

      this.choicesHolder = this.querySelector('[data-bundle-choices]');
      this.optionalHolder = this.querySelector('[data-bundle-optional]');
      this.sizesHolder = this.querySelector('[data-bundle-sizes]');
      this.itemsHolder = this.querySelector('[data-bundle-items]');
      this.addonsHolder = this.querySelector('[data-bundle-addons]');
      this.errorHolder = this.querySelector('[data-bundle-error]');
      this.submitButtons = this.querySelectorAll('[data-bundle-submit]');
      this.sticky = this.querySelector('[data-bundle-sticky]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));

      this.pickSize();
      this.render();
      this.initSticky();
    }

    disconnectedCallback() {
      this.stickyObserver?.disconnect();
    }

    // The components that decide which sizes the bundle comes in.
    get sizedComponents() {
      return this.bundle.components.filter((component) => component.matchSize && !component.optional);
    }

    // The chosen product of a component, or null for an optional step the customer left out.
    selectedProduct(componentIndex) {
      const component = this.bundle.components[componentIndex];
      return component.products[this.selectedProducts[componentIndex]] || null;
    }

    // Sizes every required size-matched component can deliver, in the order of the first one
    // (the mattress). Optional steps don't narrow the list: a product that isn't made in the
    // chosen size is simply not offered.
    commonSizes() {
      const sized = this.bundle.components
        .map((component, index) => ({component, product: this.selectedProduct(index)}))
        .filter(({component, product}) => component.matchSize && !component.optional && product);
      if (!sized.length) return [];

      const [first, ...rest] = sized.map(({product}) => productSizes(product));
      return first.filter((size) => rest.every((sizes) => sizes.includes(size)));
    }

    isSizeAvailable(size) {
      return this.bundle.components.every((component, index) => {
        if (!component.matchSize || component.optional) return true;
        const variant = variantForSize(this.selectedProduct(index), size);
        return Boolean(variant && variant.available);
      });
    }

    // Drops optional choices that aren't available in the chosen size.
    dropUnavailableOptional() {
      this.bundle.components.forEach((component, index) => {
        if (!component.optional) return;
        const product = this.selectedProduct(index);
        if (product && !this.isOffered(component, product)) this.selectedProducts[index] = -1;
      });
    }

    isOffered(component, product) {
      const variant = firstAvailable(this.componentCandidates(component, product));
      return Boolean(variant && variant.available);
    }

    pickSize() {
      const sizes = this.commonSizes();
      if (this.size && sizes.includes(this.size)) return;

      const preferred = canonicalSizes(this.bundle.defaultSize)[0];
      const available = sizes.filter((size) => this.isSizeAvailable(size));
      this.size = [preferred, ...available, ...sizes].find((size) => sizes.includes(size)) || null;
    }

    // Variants of a product that fit the bundle's size (all of them when it has no size to match).
    candidates(product, matchSize, followSize = true) {
      if (matchSize && this.sizedComponents.length) return this.size ? variantsForSize(product, this.size) : [];
      // Add-ons follow the bundle size when they come in sizes (e.g. a protector).
      if (followSize && this.size && sizeOptionIndex(product) !== -1) {
        const sized = variantsForSize(product, this.size);
        if (sized.length) return sized;
      }
      return product.variants;
    }

    variantFor(product, matchSize) {
      return firstAvailable(this.candidates(product, matchSize));
    }

    // A component that follows the bundle size offers that size; any other component offers all of
    // its variants and gets its own pickers, size included.
    componentCandidates(component, product) {
      return this.candidates(product, component.matchSize, false);
    }

    // The variant of the component's selected product for the chosen size and options.
    // A chosen value that doesn't exist for this size is dropped, starting with the oldest choice.
    componentVariant(componentIndex, override = {}) {
      const component = this.bundle.components[componentIndex];
      const product = this.selectedProduct(componentIndex);
      if (!product) return null;
      const selected = {...this.selectedOptions[componentIndex], ...override};
      const order = Object.keys(override).concat(
        [...this.optionOrder[componentIndex]].reverse(),
        extraOptionIndexes(product, !component.matchSize).map(String),
      );

      let pool = this.componentCandidates(component, product);
      new Set(order).forEach((optionIndex) => {
        if (selected[optionIndex] === undefined) return;
        const narrowed = pool.filter((variant) => variant.options[optionIndex] === selected[optionIndex]);
        if (narrowed.length) pool = narrowed;
      });

      return firstAvailable(pool);
    }

    // The bundle price of a line. At checkout a line gets one product discount, the larger of
    // the bundle's and the product's own promotion. A part marked "skip promo" doesn't get its own
    // promotion in the bundle (a campaign discount at checkout takes the line instead).
    // A variant marked down through its compare-at price gets the bundle percent off the compare-at
    // price instead of on top of the markdown, as the discount function does.
    linePrice(component, product, variant, quantity) {
      const slotPercent = component.discountPercent ?? (Number(this.bundle.discountPercent) || 0);
      const amount = variant.price * quantity;
      const compareAt = Number(variant.compareAtPrice) || 0;
      // Rounded per line (per unit for a fixed amount), the way the discount is allocated at checkout.
      const bundleDiscount =
        compareAt > variant.price
          ? Math.max(0, variant.price - Math.round((compareAt * (100 - slotPercent)) / 100)) * quantity
          : Math.round((amount * slotPercent) / 100);
      const ownPromo = component.skipPromo ? 0 : promoDiscount(product, variant, amount, quantity);
      return amount - Math.max(bundleDiscount, ownPromo);
    }

    // The lines of the bundle: every required component, and the optional ones the customer added.
    lines() {
      const lines = this.bundle.components.flatMap((component, index) => {
        const product = this.selectedProduct(index);
        if (!product) return [];
        const variant = this.componentVariant(index);
        const quantity = Number(component.quantity) || 1;
        const regular = variant ? variant.price * quantity : 0;
        // A variant marked down through its compare-at price is compared at the compare-at price,
        // so the bundle percent shown holds (the bundle takes its percent off that price).
        const compareAt = variant && Number(variant.compareAtPrice) > variant.price ? variant.compareAtPrice * quantity : 0;
        const separate = compareAt || promoPrice(product, variant, regular, quantity);
        const price = variant ? this.linePrice(component, product, variant, quantity) : 0;
        return [{component, product, variant, quantity, regular, separate, price, discount: separate - price}];
      });
      return this.shareAmount(lines);
    }

    // A bundle with a fixed amount off the set, as the discount function charges it: the amount less
    // the compare-at markdowns of the parts that share it is split over them by price (in cents,
    // the cents left over to the dearest), and each line gets the larger of its share and its own
    // promotion. Parts with a percent of their own keep it.
    shareAmount(lines) {
      const amount = Number(this.bundle.discountAmount) || 0;
      const sharing = amount > 0 ? lines.filter((line) => line.variant && line.component.discountPercent == null) : [];
      if (!sharing.length) return lines;

      const prices = sharing.map((line) => line.regular);
      const markdowns = sharing.map((line) => {
        const compareAt = Number(line.variant.compareAtPrice) || 0;
        return compareAt > line.variant.price ? (compareAt - line.variant.price) * line.quantity : 0;
      });
      const total = prices.reduce((sum, value) => sum + value, 0);
      const extra = Math.min(total, Math.max(0, amount - markdowns.reduce((sum, value) => sum + value, 0)));

      const shares = prices.map((price) => (total ? Math.floor((extra * price) / total) : 0));
      let left = extra - shares.reduce((sum, value) => sum + value, 0);
      const byPrice = prices.map((_, index) => index).sort((a, b) => prices[b] - prices[a]);
      for (let index = 0; left > 0; index = (index + 1) % byPrice.length, left--) shares[byPrice[index]] += 1;

      sharing.forEach((line, index) => {
        const ownPromo = line.component.skipPromo ? 0 : promoDiscount(line.product, line.variant, line.regular, line.quantity);
        line.price = line.regular - Math.max(shares[index], ownPromo);
        line.discount = line.separate - line.price;
      });
      return lines;
    }

    addonLines() {
      return (this.bundle.addons || []).map((product, index) => {
        const variant = this.variantFor(product, false);
        const price = variant ? promoPrice(product, variant, variant.price) : 0;
        return {product, variant, price, index, checked: this.selectedAddons.has(index)};
      });
    }

    render() {
      this.dropUnavailableOptional();

      const lines = this.lines();
      const addons = this.addonLines();
      const checkedAddons = addons.filter((addon) => addon.checked && addon.variant);

      const bundleSeparate = lines.reduce((sum, line) => sum + line.separate, 0);
      const bundlePrice = lines.reduce((sum, line) => sum + line.price, 0);
      const bundleDiscount = bundleSeparate - bundlePrice;
      const addonsTotal = checkedAddons.reduce((sum, addon) => sum + addon.price, 0);

      this.renderChoices();
      this.renderSizes();
      this.renderItems(lines);
      this.renderAddons(addons);

      this.setAll('[data-bundle-price]', formatMoney(bundlePrice));
      this.setAll('[data-bundle-compare]', formatMoney(bundleSeparate + addonsTotal));
      this.setAll('[data-bundle-discount]', `−${formatMoney(bundleDiscount)}`);
      this.setAll('[data-bundle-total]', formatMoney(bundlePrice + addonsTotal));
      this.setAll('[data-bundle-savings]', bundleDiscount > 0 ? `−${formatMoney(bundleDiscount)}` : '');
      this.setAll('[data-bundle-sticky-size]', this.size ? displaySize(this.size) : '');
      this.querySelectorAll('[data-bundle-saving-row]').forEach((row) => {
        row.hidden = bundleDiscount <= 0;
      });

      const problem = this.problem(lines);
      const message = problem || this.addError;
      this.errorHolder.hidden = !message;
      this.errorHolder.textContent = message || '';
      this.submitButtons.forEach((button) => {
        button.disabled = Boolean(problem);
      });
    }

    problem(lines) {
      if (this.sizedComponents.length && !this.commonSizes().length) return this.strings.noSizes;
      if (lines.some((line) => !line.variant || !line.variant.available)) return this.strings.unavailable;
      return null;
    }

    // Steps that decide the bundle size come before the size step; optional steps and parts with
    // a size of their own (e.g. a kids' mattress) come after it.
    renderChoices() {
      this.choicesHolder.innerHTML = this.stepsHtml(false);
      this.optionalHolder.innerHTML = this.stepsHtml(true);
    }

    stepsHtml(afterSize) {
      return this.bundle.components
        .map((component, componentIndex) => {
          const isAfterSize = Boolean(component.optional) || !component.matchSize;
          if (isAfterSize !== afterSize) return '';

          const cards =
            component.optional || component.products.length > 1 ? this.choiceCards(component, componentIndex) : '';
          const options = this.optionPickers(component, componentIndex);
          if (!cards && !options) return '';

          return `
            <div class="bundle-product__step">
              <p class="bundle-product__step-title">
                ${escapeHtml(component.label)}
                ${component.optional ? `<span class="bundle-product__step-note">${escapeHtml(this.strings.optional)}</span>` : ''}
              </p>
              ${cards}
              ${options}
            </div>`;
        })
        .join('');
    }

    choiceCards(component, componentIndex) {
      const quantity = Number(component.quantity) || 1;
      const prices = component.products.map((product, productIndex) => {
        const variant =
          this.selectedProducts[componentIndex] === productIndex
            ? this.componentVariant(componentIndex)
            : firstAvailable(this.componentCandidates(component, product));
        return variant ? this.linePrice(component, product, variant, quantity) : null;
      });
      const known = prices.filter((price) => price !== null);
      const cheapest = known.length ? Math.min(...known) : 0;

      const cards = component.products.map((product, productIndex) => {
        const selected = this.selectedProducts[componentIndex] === productIndex;
        const price = prices[productIndex];
        const offered = !component.optional || this.isOffered(component, product);

        // A required step shows what a product costs on top of the cheapest one;
        // an optional step shows what adding the product costs.
        let note = '';
        if (!offered) note = this.strings.notInSize;
        else if (component.optional && price !== null) note = price > 0 ? `+${formatMoney(price)}` : this.strings.free;
        else if (price !== null && price > cheapest) note = `+${formatMoney(price - cheapest)}`;

        return `
          <button type="button" class="bundle-product__choice${selected ? ' is-selected' : ''}"
            role="radio" aria-checked="${selected}" ${offered ? '' : 'disabled'}
            data-choice-component="${componentIndex}" data-choice-product="${productIndex}">
            ${product.image ? `<img src="${product.image}" alt="" loading="lazy" width="120" height="80">` : ''}
            <span class="bundle-product__choice-title">${quantity > 1 ? `${quantity} × ` : ''}${escapeHtml(product.title)}</span>
            ${note ? `<span class="bundle-product__choice-delta">${escapeHtml(note)}</span>` : ''}
          </button>`;
      });

      if (component.optional) {
        const selected = this.selectedProducts[componentIndex] === -1;
        cards.unshift(`
          <button type="button" class="bundle-product__choice bundle-product__choice--none${selected ? ' is-selected' : ''}"
            role="radio" aria-checked="${selected}"
            data-choice-component="${componentIndex}" data-choice-product="-1">
            <span class="bundle-product__choice-title">${escapeHtml(this.strings.none)}</span>
          </button>`);
      }

      return `<div class="bundle-product__choice-list" role="radiogroup" aria-label="${escapeHtml(component.label)}">${cards.join('')}</div>`;
    }

    // Pickers for the selected product's options beyond size (e.g. base height, legs).
    optionPickers(component, componentIndex) {
      const product = this.selectedProduct(componentIndex);
      const current = this.componentVariant(componentIndex);
      if (!product || !current) return '';

      const quantity = Number(component.quantity) || 1;
      const candidates = this.componentCandidates(component, product);

      return extraOptionIndexes(product, !component.matchSize)
        .map((optionIndex) => {
          const values = [...new Set(candidates.map((variant) => variant.options[optionIndex]))];
          if (values.length < 2) return '';

          const name = product.options[optionIndex];
          const currentPrice = this.linePrice(component, product, current, quantity);
          const choices = values.map((value) => {
            const variant = this.componentVariant(componentIndex, {[optionIndex]: value});
            const price = variant ? this.linePrice(component, product, variant, quantity) : currentPrice;
            const difference = price - currentPrice;
            const delta = difference ? ` (${difference > 0 ? '+' : '−'}${formatMoney(Math.abs(difference))})` : '';
            return {value, delta, selected: current.options[optionIndex] === value};
          });
          const attributes = `data-option-component="${componentIndex}" data-option-index="${optionIndex}"`;

          const picker =
            values.length > 4
              ? `<select class="bundle-product__option-select" aria-label="${escapeHtml(name)}" ${attributes}>
                  ${choices
                    .map(
                      ({value, delta, selected}) =>
                        `<option value="${escapeHtml(value)}"${selected ? ' selected' : ''}>${escapeHtml(value)}${delta}</option>`,
                    )
                    .join('')}
                </select>`
              : `<div class="bundle-product__sizes" role="radiogroup" aria-label="${escapeHtml(name)}">
                  ${choices
                    .map(
                      ({value, delta, selected}) => `
                        <button type="button" class="bundle-product__size${selected ? ' is-selected' : ''}"
                          role="radio" aria-checked="${selected}" ${attributes} data-option-value="${escapeHtml(value)}">
                          ${escapeHtml(value)}${delta ? `<small>${delta}</small>` : ''}
                        </button>`,
                    )
                    .join('')}
                </div>`;

          return `
            <div class="bundle-product__option">
              <p class="bundle-product__option-label">${escapeHtml(name)}</p>
              ${picker}
            </div>`;
        })
        .join('');
    }

    selectOption(componentIndex, optionIndex, value) {
      this.selectedOptions[componentIndex][optionIndex] = value;
      // Most recent choices win when a combination doesn't exist for the chosen size.
      this.optionOrder[componentIndex] = this.optionOrder[componentIndex]
        .filter((index) => index !== String(optionIndex))
        .concat(String(optionIndex));
      this.render();
    }

    renderSizes() {
      const sizes = this.commonSizes();
      const step = this.sizesHolder.closest('.bundle-product__step');
      step.hidden = !sizes.length;

      this.sizesHolder.innerHTML = sizes
        .map((size) => {
          const selected = size === this.size;
          const available = this.isSizeAvailable(size);
          return `
            <button type="button" class="bundle-product__size${selected ? ' is-selected' : ''}${available ? '' : ' is-unavailable'}"
              role="radio" aria-checked="${selected}" data-size="${size}">
              ${displaySize(size)}
            </button>`;
        })
        .join('');
    }

    renderItems(lines) {
      this.itemsHolder.innerHTML = lines
        .map(({component, product, variant, quantity, regular, separate, price, discount}) => {
          const image = (variant && variant.image) || product.image;
          const variantTitle = variant && variant.title !== 'Default Title' ? variant.title : '';
          const stock = variant && variant.available ? this.strings.inStock : this.strings.outOfStock;
          return `
            <li class="bundle-product__item">
              ${image ? `<img class="bundle-product__item-image" src="${image}" alt="" loading="lazy" width="120" height="80">` : '<span class="bundle-product__item-image"></span>'}
              <div class="bundle-product__item-body">
                <p class="bundle-product__item-label">${escapeHtml(component.label)}</p>
                <a class="bundle-product__item-title" href="${product.url}">${quantity > 1 ? `${quantity} × ` : ''}${escapeHtml(product.title)}</a>
                <p class="bundle-product__item-meta">
                  ${variantTitle ? `<span>${escapeHtml(variantTitle)}</span>` : ''}
                  <span class="${variant && variant.available ? 'is-in-stock' : 'is-out-of-stock'}">${stock}</span>
                </p>
              </div>
              <div class="bundle-product__item-price">
                ${discount > 0 ? `<s>${formatMoney(separate)}</s>` : ''}
                <span>${regular > 0 && price <= 0 ? this.strings.free : formatMoney(price)}</span>
              </div>
            </li>`;
        })
        .join('');
    }

    renderAddons(addons) {
      if (!this.addonsHolder) return;

      this.addonsHolder.innerHTML = addons
        .map(({product, variant, price, index, checked}) => {
          if (!variant) return '';
          const image = variant.image || product.image;
          const variantTitle = variant.title !== 'Default Title' ? variant.title : '';
          const id = `BundleAddon-${this.dataset.sectionId}-${index}`;
          return `
            <li class="bundle-product__item bundle-product__item--addon${variant.available ? '' : ' is-unavailable'}">
              <input type="checkbox" id="${id}" data-addon="${index}" ${checked ? 'checked' : ''} ${variant.available ? '' : 'disabled'}>
              ${image ? `<img class="bundle-product__item-image" src="${image}" alt="" loading="lazy" width="120" height="80">` : '<span class="bundle-product__item-image"></span>'}
              <label class="bundle-product__item-body" for="${id}">
                <span class="bundle-product__item-title">${escapeHtml(product.title)}</span>
                <span class="bundle-product__item-meta">
                  ${variantTitle ? `<span>${escapeHtml(variantTitle)}</span>` : ''}
                  ${variant.available ? '' : `<span class="is-out-of-stock">${this.strings.outOfStock}</span>`}
                </span>
              </label>
              <div class="bundle-product__item-price"><span>+${formatMoney(price)}</span></div>
            </li>`;
        })
        .join('');
    }

    setAll(selector, value) {
      this.querySelectorAll(selector).forEach((element) => {
        element.textContent = value;
      });
    }

    onClick(event) {
      if (!event.target.closest('[data-bundle-submit]')) this.addError = null;

      const choice = event.target.closest('[data-choice-component]');
      if (choice) {
        const componentIndex = Number(choice.dataset.choiceComponent);
        this.selectedProducts[componentIndex] = Number(choice.dataset.choiceProduct);
        this.selectedOptions[componentIndex] = {};
        this.optionOrder[componentIndex] = [];
        this.pickSize();
        this.render();
        return;
      }

      const option = event.target.closest('[data-option-value]');
      if (option) {
        this.selectOption(Number(option.dataset.optionComponent), Number(option.dataset.optionIndex), option.dataset.optionValue);
        return;
      }

      const size = event.target.closest('[data-size]');
      if (size) {
        this.size = size.dataset.size;
        this.render();
        return;
      }

      if (event.target.closest('[data-bundle-submit]')) {
        this.addToCart();
      }
    }

    onChange(event) {
      const select = event.target.closest('select[data-option-component]');
      if (select) {
        this.addError = null;
        this.selectOption(Number(select.dataset.optionComponent), Number(select.dataset.optionIndex), select.value);
        return;
      }

      const addon = event.target.closest('[data-addon]');
      if (!addon) return;
      this.addError = null;
      const index = Number(addon.dataset.addon);
      if (addon.checked) this.selectedAddons.add(index);
      else this.selectedAddons.delete(index);
      this.render();
    }

    initSticky() {
      const mainButton = this.querySelector('.bundle-product__submit');
      if (!this.sticky || !mainButton || !('IntersectionObserver' in window)) return;

      this.stickyObserver = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting;
        this.sticky.classList.toggle('is-visible', visible);
        this.sticky.setAttribute('aria-hidden', String(!visible));
        this.sticky.querySelectorAll('button').forEach((button) => {
          button.tabIndex = visible ? 0 : -1;
        });
      });
      this.stickyObserver.observe(mainButton);
    }

    async addToCart() {
      const lines = this.lines();
      if (this.problem(lines)) return;

      const bundleId = `${this.bundle.handle}-${Date.now().toString(36)}`;
      const items = lines.map(({variant, quantity}) => ({
        id: variant.id,
        quantity,
        properties: {_bundle: this.bundle.handle, _bundle_id: bundleId},
      }));
      this.addonLines()
        .filter((addon) => addon.checked && addon.variant && addon.variant.available)
        .forEach((addon) => items.push({id: addon.variant.id, quantity: 1}));

      this.addError = null;
      this.setLoading(true);

      try {
        const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items}),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || response.statusText);

        this.onAdded();
      } catch (error) {
        console.error('[bundle-product] Add to cart failed', error);
        this.addError = error.message || this.strings.addError;
      } finally {
        this.setLoading(false);
        // Re-applies the disabled state for an unavailable selection.
        this.render();
      }
    }

    onAdded() {
      const cartDrawer = document.querySelector('cart-drawer');

      if (this.dataset.cartType !== 'drawer' || !cartDrawer) {
        window.location.href = window.theme?.routes?.cart_url || '/cart';
        return;
      }

      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
      cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
    }

    setLoading(loading) {
      this.submitButtons.forEach((button) => {
        button.classList.toggle('is-loading', loading);
        button.disabled = loading;
      });
      const text = this.querySelector('[data-bundle-submit-text]');
      if (!text) return;
      if (loading) {
        this.buttonText = this.buttonText || text.textContent;
        text.textContent = this.strings.adding;
      } else if (this.buttonText) {
        text.textContent = this.buttonText;
      }
    }
  }

  customElements.define('bundle-product', BundleProduct);
})();
